import {PublicKey, TransactionInstruction} from "@solana/web3.js";
import {getOrCreateAssociatedTokenAccount, TOKEN_PROGRAM_ID} from "@solana/spl-token";
import { LAMPORTS_PER_SOL} from '@solana/web3.js';

import {solana} from '@web3modal/solana/chains';
import {connection} from '../../utils/connection';
import {ETH_RPC_URL, BASE_RPC_URL, MONAD_RPC_URL} from '../../configs/config';

// SPL mint addresses (mainnet)
const USDT_MINT_ADDRESS = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';
const USDC_MINT_ADDRESS = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

// ERC-20 token addresses
const ETH_USDT_ADDRESS = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
const ETH_USDC_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const BASE_USDC_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
// Note: Base network does not have USDT
const MONAD_USDC_ADDRESS = '0x754704Bc059F8C67012fEd69BC8A327a5aafb603';
const MONAD_USDT_ADDRESS = process.env.REACT_APP_MONAD_USDT_ADDRESS || null;
const MEMO_PROGRAM_ID = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");

const ETH_CHAIN_ID = '0x1';
const BASE_CHAIN_ID = '0x2105';
const MONAD_CHAIN_ID = '0x8f';

const buildTokenConfig = (address, decimals = 6) => {
    if (!address) {
        return null;
    }
    return {address, decimals};
};

export const EVM_CHAIN_CONFIG = {
    ethereum: {
        key: 'ethereum',
        label: 'Ethereum',
        chainId: ETH_CHAIN_ID,
        rpcUrls: [ETH_RPC_URL],
        blockExplorerUrls: ['https://etherscan.io'],
        nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
            displayName: 'ETH'
        },
        supportedTokens: {
            USDC: buildTokenConfig(ETH_USDC_ADDRESS),
            USDT: buildTokenConfig(ETH_USDT_ADDRESS)
        }
    },
    base: {
        key: 'base',
        label: 'Base',
        chainId: BASE_CHAIN_ID,
        rpcUrls: [BASE_RPC_URL],
        blockExplorerUrls: ['https://basescan.org'],
        nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
            displayName: 'ETH on Base'
        },
        supportedTokens: {
            USDC: buildTokenConfig(BASE_USDC_ADDRESS),
            USDT: null
        }
    },
    monad: {
        key: 'monad',
        label: 'Monad',
        chainId: MONAD_CHAIN_ID,
        rpcUrls: [MONAD_RPC_URL],
        blockExplorerUrls: ['https://mainnet-beta.monvision.io'],
        nativeCurrency: {
            name: 'Monad',
            symbol: 'MON',
            decimals: 18,
            displayName: 'MON'
        },
        supportedTokens: {
            USDC: buildTokenConfig(MONAD_USDC_ADDRESS),
            USDT: buildTokenConfig(MONAD_USDT_ADDRESS)
        }
    }
};

export const isEvmChain = (chain) => chain && chain !== 'solana';

export const getEvmChainConfig = (chain) => {
    if (!isEvmChain(chain)) {
        return null;
    }
    return EVM_CHAIN_CONFIG[chain] || null;
};

export const getNativeCurrencyDisplayName = (chain) => {
    if (!isEvmChain(chain)) {
        return 'SOL';
    }
    const config = getEvmChainConfig(chain);
    if (!config) {
        return 'ETH';
    }
    return config.nativeCurrency?.displayName || config.nativeCurrency?.symbol || 'ETH';
};

export const getExplorerBaseUrl = (chain) => {
    if (!isEvmChain(chain)) {
        return 'https://solscan.io';
    }
    const config = getEvmChainConfig(chain);
    return config?.blockExplorerUrls?.[0] || 'https://etherscan.io';
};


// For wallet Connect modal config
export const chains = [solana];
export const projectId = '19bcd68b0a6d5ed2f17caebebd775ae6';
export const metadata = {
    name: 'PrismaxAI',
    description: 'PrismaxAI',
    url: 'https://app.prismax.ai',
    icons: ['https://app.prismax.ai/favicon.svg']
};



export const fetchBalancesAndNotifyParent = async (
    provider,
    publicKey,
    onWalletConnected,
    showNotification,
    selectedChain = 'solana'
) => {
    try {
        const userPubkey = new PublicKey(publicKey);

        const lamports = await connection.getBalance(userPubkey);
        const solBalance = lamports / LAMPORTS_PER_SOL;
        if (solBalance <= 0) {
            const currencyName = isEvmChain(selectedChain)
                ? getNativeCurrencyDisplayName(selectedChain)
                : 'SOL';
            showNotification(
                'error',
                `Please connect a wallet with a positive ${currencyName} balance or log in with your email to continue.`
            );
            return false;
        }

        const payer = {
            publicKey: userPubkey,
            signTransaction: async (tx) => provider.signTransaction(tx),
        };

        let usdcBalance = 0;
        let usdtBalance = 0;

        try {
            const usdcAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                payer,
                new PublicKey(USDC_MINT_ADDRESS),
                userPubkey
            );
            usdcBalance = Number(usdcAccount.amount) / 1_000_000;
        } catch (err) {
            console.error('Error fetching USDC balance:', err);
        }

        try {
            const usdtAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                payer,
                new PublicKey(USDT_MINT_ADDRESS),
                userPubkey
            );
            usdtBalance = Number(usdtAccount.amount) / 1_000_000;
        } catch (err) {
            console.error('Error fetching USDT balance:', err);
        }

        onWalletConnected?.(publicKey, usdtBalance, usdcBalance);

        return true; // ✅
    } catch (err) {
        console.error('Error fetching balances:', err);
        showNotification?.('error', 'Failed to fetch wallet balances.');
        return false;
    }
};
// same as above function ,but instead of pass to parent hook , just pass 3 constant out
export const fetchTokenBalances = async (provider, publicKey) => {
    try {
        // const connection = new Connection(RPC_URL, "confirmed");
        const userPubkey = new PublicKey(publicKey);

        const payer = {
            publicKey: userPubkey,
            signTransaction: async (tx) => {
                return await provider.signTransaction(tx);
            }
        };

        let usdcBalance = 0;
        let usdtBalance = 0;
        // USDC
        try {
            const usdcMintPubkey = new PublicKey(USDC_MINT_ADDRESS);
            const usdcAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                payer,
                usdcMintPubkey,
                userPubkey
            );
            usdcBalance = Number(usdcAccount.amount) / 1_000_000;
        } catch (err) {
            console.error("Error fetching USDC balance:", err);
        }

        // USDT
        try {
            const usdtMintPubkey = new PublicKey(USDT_MINT_ADDRESS);
            const usdtAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                payer,
                usdtMintPubkey,
                userPubkey
            );
            usdtBalance = Number(usdtAccount.amount) / 1_000_000;
        } catch (err) {
            console.error("Error fetching USDT balance:", err);
        }


        return {
            usdcBalance,
            usdtBalance,
        };
    } catch (err) {
        console.error("Error fetching balances:", err);
    }
};

export async function fetchThreeTokenBalancesWithoutProvider(walletAddressStr, onWalletConnected) {
    // const connection = new Connection(RPC_URL, "confirmed");
    let usdtBalance = 0;
    let usdcBalance = 0;
    let walletPublicKey
    try {
        walletPublicKey = new PublicKey(walletAddressStr);
        // 1) Get all token accounts owned by this wallet
        const response = await connection.getParsedTokenAccountsByOwner(
            walletPublicKey,
            {programId: new PublicKey("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA")}
        );

        // 2) Initialize balances


        // 3) Loop through all token accounts, match the mint, parse its balance
        for (const {account} of response.value) {
            const info = account.data.parsed.info;
            const mint = info.mint;
            const amountStr = info.tokenAmount?.amount || "0";
            const decimals = info.tokenAmount?.decimals || 0;
            const parsedBalance = Number(amountStr) / 10 ** decimals;

            if (mint === USDT_MINT_ADDRESS) {
                usdtBalance = parsedBalance;
            } else if (mint === USDC_MINT_ADDRESS) {
                usdcBalance = parsedBalance;
            }
            if (onWalletConnected) {
                onWalletConnected(walletAddressStr, usdtBalance, usdcBalance);
            }
            return {usdtBalance, usdcBalance};
        }
    } catch (err) {
        console.error("Error fetching balances:", err);
        if (onWalletConnected) {
            onWalletConnected(walletAddressStr, usdtBalance, usdcBalance);
        }
        return {usdtBalance, usdcBalance};
    }

}

export const getTokenAccountFromWalletAddress = async (walletToQuery, mintAddress) => {
    try {
        const walletPubkey = new PublicKey(walletToQuery);
        const mintPubkey = new PublicKey(mintAddress);

        const tokenAccounts = await connection.getTokenAccountsByOwner(
            walletPubkey,
            { mint: mintPubkey }
        );

        if (tokenAccounts.value.length === 0) {
            console.log("No token account found");
            return null;
        }

        return tokenAccounts.value[0].pubkey.toBase58();

    } catch (error) {
        console.error("Error getting token account:", error);
        return null;
    }
};

export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function sleep2(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function addMemoInstruction(transaction, text) {
    const memoIx = new TransactionInstruction({
        keys: [],
        programId: MEMO_PROGRAM_ID,
        data: Buffer.from(text, 'utf8'),
    });
    transaction.add(memoIx);
}

// EVM Balance Fetching Functions
export const fetchEVMBalancesAndNotifyParent = async (
    provider,
    address,
    onWalletConnected,
    showNotification,
    selectedChain = 'ethereum'
) => {
    try {
    const chainConfig = getEvmChainConfig(selectedChain) || EVM_CHAIN_CONFIG.ethereum;

    // Get native token balance (ETH)
    const balanceWei = await provider.request({
        method: 'eth_getBalance',
        params: [address, 'latest']
    });
    const nativeBalance = parseInt(balanceWei, 16) / 1e18;
    
    // Check minimum native balance requirement
    if (nativeBalance <= 0) {
        const currencyName = chainConfig.nativeCurrency?.displayName
            || `${chainConfig.nativeCurrency?.symbol || 'ETH'} on ${chainConfig.label}`;
            showNotification(
                'error',
                `Please connect a wallet with a positive ${currencyName} balance or log in with your email to continue.`
            );
            return false;
        }

        let usdcBalance = 0;
        let usdtBalance = 0;

        const usdcToken = chainConfig.supportedTokens?.USDC;
        if (usdcToken?.address) {
            try {
                const usdcData = await provider.request({
                    method: 'eth_call',
                    params: [{
                        to: usdcToken.address,
                        data: '0x70a08231000000000000000000000000' + address.slice(2) // balanceOf(address)
                    }, 'latest']
                });
                
                if (usdcData && usdcData !== '0x') {
                    usdcBalance = parseInt(usdcData, 16) / Math.pow(10, usdcToken.decimals ?? 6);
                }
            } catch (err) {
                console.error('Error fetching USDC balance:', err);
            }
        }

        // Get USDT balance if supported
        const usdtToken = chainConfig.supportedTokens?.USDT;
        if (usdtToken?.address) {
            try {
                const usdtData = await provider.request({
                    method: 'eth_call',
                    params: [{
                        to: usdtToken.address,
                        data: '0x70a08231000000000000000000000000' + address.slice(2) // balanceOf(address)
                    }, 'latest']
                });
                
                if (usdtData && usdtData !== '0x') {
                    const decimals = usdtToken.decimals ?? 6;
                    usdtBalance = parseInt(usdtData, 16) / Math.pow(10, decimals);
                }
            } catch (err) {
                console.error('Error fetching USDT balance:', err);
            }
        }

        // Notify parent component with EVM balances
        onWalletConnected?.(address, usdtBalance, usdcBalance, nativeBalance);
        
        return true;
    } catch (err) {
        console.error('Error fetching EVM balances:', err);
        showNotification?.('error', 'Failed to fetch wallet balances.');
        return false;
    }
};

// Simplified version that just returns balance data
export const fetchEVMTokenBalances = async (provider, address, selectedChain = 'ethereum') => {
    try {
        const chainConfig = getEvmChainConfig(selectedChain) || EVM_CHAIN_CONFIG.ethereum;
        // Get native token balance
        const balanceWei = await provider.request({
            method: 'eth_getBalance',
            params: [address, 'latest']
        });
        const nativeBalance = parseInt(balanceWei, 16) / 1e18;

        let usdcBalance = 0;
        let usdtBalance = 0;
        const usdcToken = chainConfig.supportedTokens?.USDC;
        if (usdcToken?.address) {
            try {
                const usdcData = await provider.request({
                    method: 'eth_call',
                    params: [{
                        to: usdcToken.address,
                        data: '0x70a08231000000000000000000000000' + address.slice(2)
                    }, 'latest']
                });
                if (usdcData && usdcData !== '0x') {
                    usdcBalance = parseInt(usdcData, 16) / Math.pow(10, usdcToken.decimals ?? 6);
                }
            } catch (err) {
                console.error('Error fetching USDC balance:', err);
            }
        }

        const usdtToken = chainConfig.supportedTokens?.USDT;
        if (usdtToken?.address) {
            try {
                const usdtData = await provider.request({
                    method: 'eth_call',
                    params: [{
                        to: usdtToken.address,
                        data: '0x70a08231000000000000000000000000' + address.slice(2)
                    }, 'latest']
                });
                if (usdtData && usdtData !== '0x') {
                    usdtBalance = parseInt(usdtData, 16) / Math.pow(10, usdtToken.decimals ?? 6);
                }
            } catch (err) {
                console.error('Error fetching USDT balance:', err);
            }
        }

        return {
            nativeBalance,
            usdcBalance,
            usdtBalance
        };
    } catch (err) {
        console.error('Error fetching EVM balances:', err);
        return {
            nativeBalance: 0,
            usdcBalance: 0,
            usdtBalance: 0
        };
    }
};
